import DOMPurify from 'dompurify';

/**
 * Sanitizes rich-text HTML before it's rendered with
 * `dangerouslySetInnerHTML`.
 *
 * Why this exists: RichTextEditor.tsx's CKEditor config enables
 * `GeneralHtmlSupport` with an allow-list that matches any tag name and
 * allows any classes, attributes, and styles — i.e. it accepts any tag/attribute
 * an admin pastes or types in (including via the source-editing toolbar
 * button), and that HTML was rendered completely unsanitized in
 * ViewContent.tsx. An admin token sits in plain localStorage (see
 * AUDIT_FIX_LOG.md Critical #2) — one bad paste into a content field is a
 * direct path to stealing another admin's session the next time they view
 * that page. This is the single sanitize-on-render choke point; every
 * `dangerouslySetInnerHTML` of CMS/rich-text content in this app should go
 * through this, not render raw HTML directly.
 *
 * Allow-list intentionally covers normal rich-text authoring (headings,
 * lists, tables, links, images, basic inline styling) and nothing that can
 * execute script or navigate via a dangerous protocol: no <script>,
 * <iframe>, <object>, <embed>, event handler attributes (onerror, onclick,
 * ...), or javascript:/data: URLs in href/src.
 */
export function sanitizeHtml(html: string): string {
  if (!html) return '';

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'hr',
      'div',
      'span',
      'a',
      'b',
      'strong',
      'i',
      'em',
      'u',
      's',
      'strike',
      'sub',
      'sup',
      'blockquote',
      'pre',
      'code',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'table',
      'thead',
      'tbody',
      'tfoot',
      'tr',
      'th',
      'td',
      'img',
      'figure',
      'figcaption',
    ],
    ALLOWED_ATTR: [
      'href',
      'target',
      'rel',
      'src',
      'alt',
      'title',
      'width',
      'height',
      'class',
      'style',
      'colspan',
      'rowspan',
    ],
    ALLOWED_URI_REGEXP:
      /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'style', 'form'],
    FORBID_ATTR: [
      'onerror',
      'onload',
      'onclick',
      'onmouseover',
      'onfocus',
      'onblur',
    ],
  });
}
